import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useDebounce } from "react-use";
import {Radio, Select, Input, Button} from "antd";
import LoadingBanner from "../../components/LoadingBanner";
import TalentProvider from "../../providers/TalentProvider";
import TalentTile, { InvitedTalentTile } from "../talent/TalentTile";
import { useTalentPool } from "../../providers/TalentPoolProvider";
import TablePagination from "../../components/TablePagination";
import EmptyFeedback from "../../components/EmptyFeedback";
import ComponentError from "../../components/errors/ComponentError";
import InviteByEmailModal from "./components/InviteByEmailModal";
import InviteByGroupModal from "./components/InviteByGroupModal";

const EMPTY_MESSAGE = {
    "-1": 'Favorited talent will appear here',
    "-2": 'Past hired talent will appear here',
    "-3": 'Invited talent will appear here',
    "all": 'No talent added yet',
}

const TalentPoolManage = () => {
    const { talentPoolId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showingActiveTalent, setShowingActiveTalent] = useState(null)
    const {
        talentPool,
        isLoading,
        refetch,
        error,
        onResetMemberData,
        isDisabled,
        onChangeSort,
        onUpdateTalent,
        onRemoveTalentFromPool,
        SORT_OPTIONS_MEMBERS
    } = useTalentPool();

    useEffect(() => {
        if (!isLoading  && !!talentPool && talentPoolId) {
            setShowingActiveTalent(talentPoolId !== 'bring-your-own-talent' || (talentPool?.talent_users?.length > 0))
            setInputValue('')
        }
    }, [isLoading, talentPool,talentPoolId])

    const [inputValue, setInputValue] = useState(searchParams.get('search') || '');

    const groupType = useMemo(() => {
        if (!talentPool) return '';
        if (talentPool.is_default) {
            return talentPool.isWrippleRecommendedPool?.() ? talentPool.name : TB.startCase(talentPoolId)
        }
        if (talentPool.is_personal) return 'Personal Talent Pool';
        return 'Company Talent Pool';
    }, [talentPool])

    const [, cancel] = useDebounce(
        () => {
            // setDebouncedValue(inputValue);
            const params = new URLSearchParams(searchParams);
            if (inputValue.length) {
                params.set('search', inputValue);
            } else {
                params.delete('search');
            }
            return setSearchParams(params, { replace: true });
        }, 600, [inputValue]
    );

    return (
        <div className="talent-pool-manage">

            <div className="">

                {!isLoading && error && (
                    <ComponentError>
                        <button
                            type="button"
                            className="btn btn-primary mt-2"
                            onClick={refetch}
                        >
                            Try Again
                        </button>
                    </ComponentError>
                )}

                {(isLoading && !error) ? (
                    <LoadingBanner />
                ) : !error && (

                    <div className="flex flex-col items-stretch gap-4">
                        {/* Only shown on Talent Invitations page */}

                        {talentPoolId === 'bring-your-own-talent' && (
                            <div className="flex flex-col items-center mb-2">
                                <Radio.Group
                                    className="invitations-toggle-radio"
                                    options={[
                                        {
                                            label: `Awaiting Acceptance (${talentPool?.talent_invites?.length || 0})`,
                                            value: false,
                                        }, {
                                            label: `Accepted (${talentPool?.talent_users?.length || 0})`,
                                            value: true,
                                        }
                                    ]}
                                    onChange={async (e) => {
                                        if (!showingActiveTalent && e.target.value) {
                                            await onResetMemberData();
                                        }
                                        console.log('e', e.target.value)
                                        return setShowingActiveTalent(e.target.value)
                                    }}
                                    value={showingActiveTalent}
                                    disabled={isDisabled}
                                    optionType="button"
                                    buttonStyle="solid"
                                />
                            </div>
                        )}

                        <div className="flex flex-col gap-4 justify-between items-stretch">

                            {!showingActiveTalent ? (
                                <React.Fragment>
                                    <p className="text-primary font-bold text-base">
                                        Awaiting Acceptance&nbsp;
                                        <span className="text-secondary font-bold">
                                            ({talentPool?.talent_invites?.length})
                                        </span>
                                    </p>
                                    <div className="flex flex-wrap gap-6 justify-center">
                                        {!talentPool?.talent_invites?.length ? (
                                            <EmptyFeedback>

                                                <div className="flex flex-col items-center">
                                                    <p className="text-sm font-medium text-center pb-6">
                                                        Invited talent will appear here
                                                    </p>

                                                    <div className="flex flex-row items-center gap-3">
                                                        <InviteByEmailModal useDarkTheme={true}/>
                                                        <InviteByGroupModal />
                                                    </div>
                                                </div>

                                            </EmptyFeedback>
                                        ) : talentPool?.talent_invites?.map((user, index) => {
                                            return (
                                                <div key={`${user.email}-${index}`}
                                                     className="max-w-[342px] min-w-[342px]">
                                                    <TalentProvider
                                                        talent={user}
                                                        isDisabled={isDisabled}
                                                        onUpdated={updatedTalent => {
                                                            console.log('onUpdated', updatedTalent)
                                                            onUpdateTalent(updatedTalent)
                                                        }}
                                                        criteria={{}}
                                                    >
                                                        <InvitedTalentTile onRemoveFromPool={!talentPool.canAddRemoveTalent() ? undefined : async () => {
                                                            let updated = await onRemoveTalentFromPool(user);
                                                            if (updated) {
                                                                await setInputValue('');
                                                            }
                                                        }}/>
                                                    </TalentProvider>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <Input
                                        size="large"
                                        className="w-full p-3"
                                        placeholder="Search By Name"
                                        value={inputValue}
                                        disabled={isLoading || !talentPool?.talent_users?.length}
                                        onChange={({ currentTarget }) => setInputValue(currentTarget.value)}
                                    />
                                    <div className="flex flex-row justify-between items-center">
                                        {talentPoolId === 'bring-your-own-talent' ? (
                                            <p className="text-primary font-bold text-base">
                                                Accepted&nbsp;
                                                <span className="text-secondary font-bold">
                                                    ({talentPool?.talent_users?.length})
                                                </span>
                                            </p>
                                        ) : (
                                            <p className="text-primary font-bold text-lg">
                                                {groupType}&nbsp;
                                                {!!talentPool?.talent_users?.length && talentPoolId !== 'bring-your-own-talent' && (
                                                    <span className="text-secondary font-bold">
                                                        ({talentPool?.talent_users?.length})
                                                    </span>
                                                )}
                                            </p>
                                        )}

                                        <div className="flex flex-row gap-1 items-center">
                                            <p>Sort:</p>
                                            <Select
                                                defaultValue="avg_review_rating"
                                                value={SORT_OPTIONS_MEMBERS[searchParams.get('sort_by') || 'avg_review_rating']}
                                                onChange={(key) => onChangeSort(key)}
                                                bordered={false}
                                                options={TB.map(SORT_OPTIONS_MEMBERS, (label, value) => ({
                                                    value,
                                                    label
                                                }))}
                                                disabled={isLoading}
                                                popupClassName="min-w-[210px]"
                                                getPopupContainer={() => document.getElementById('talent-pool-manage-container')}
                                                className="text-primary"
                                                placement="bottomRight"
                                            />
                                        </div>
                                    </div>
                                    {talentPool && !isLoading && (
                                        <TablePagination
                                            isDisabled={isLoading}
                                            currentPage={talentPool.current_page}
                                            useParams
                                            pagesCount={talentPool.last_page}
                                            total={talentPool.total}
                                            perPage={talentPool.per_page}
                                        />
                                    )}
                                    <div className="browse-list full-card-list">

                                        {!talentPool?.talent_users?.length ? (
                                            <EmptyFeedback>
                                                {!!new URLSearchParams(searchParams).get('search') ? (
                                                    <div className="flex flex-col items-center">
                                                        <p className="text-sm font-medium text-center pb-6">
                                                            No talent found in your search
                                                        </p>

                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => {
                                                                setInputValue('')
                                                                const params = new URLSearchParams(searchParams);
                                                                params.delete('search');
                                                                setSearchParams(params, { replace: true });
                                                            }}
                                                        >
                                                            Reset Search
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center">
                                                        <p className="text-sm font-medium text-center pb-6">
                                                            {EMPTY_MESSAGE[talentPool?.id < 0 ? talentPool.id : 'all']}
                                                        </p>

                                                        {talentPool.id > 0 && (
                                                            <Link
                                                                type="button"
                                                                to={`/client/talent-pool/${talentPool.id}/add-talent`}
                                                                className="btn btn-primary flex gap-2"
                                                            >
                                                                <i className="fas fa-plus" /> Add Talent
                                                            </Link>
                                                        )}
                                                    </div>
                                                )}
                                            </EmptyFeedback>
                                        ) : (
                                            <div className="flex flex-wrap gap-6 justify-center">
                                                <div className="flex flex-wrap gap-6 justify-center">
                                                    {talentPool?.talent_users?.map(user => (
                                                        <div key={`tal-${user.id}`}
                                                            className="max-w-[342px] min-w-[342px] flex flex-col items-stretch">
                                                            <TalentProvider
                                                                talent={user}
                                                                onUpdated={updatedTalent => {
                                                                    console.log('onUpdated', updatedTalent)
                                                                    onUpdateTalent(updatedTalent)
                                                                }}
                                                                isDisabled={isDisabled}
                                                                criteria={{}}
                                                            >
                                                                <TalentTile
                                                                    includeRating
                                                                    onRequest={(requestCallback) => requestCallback()}
                                                                    onUpdated={updatedTalent => {
                                                                        console.log('onUpdated', updatedTalent)
                                                                        onUpdateTalent(updatedTalent)
                                                                    }}
                                                                    isDisabled={isLoading || isDisabled}
                                                                    placeholder={isLoading}
                                                                    // onRemoveFromPool={}
                                                                >
                                                                    <div className="bg-white flex flex-col items-stretch px-4 py-6 justify-center">
                                                                        <Button disabled={isDisabled || !talentPool.canAddRemoveTalent()} type="primary"  onClick={async () => {
                                                                            let updated = await onRemoveTalentFromPool(user);
                                                                            if (updated) {
                                                                                await setInputValue('');
                                                                            }
                                                                        }}>
                                                                            Remove from Pool
                                                                        </Button>
                                                                    </div>

                                                                </TalentTile>
                                                            </TalentProvider>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {talentPool && !isLoading && (
                                        <TablePagination
                                            isDisabled={isLoading}
                                            currentPage={talentPool.current_page}
                                            useParams
                                            pagesCount={talentPool.last_page}
                                            total={talentPool.total}
                                            perPage={talentPool.per_page}
                                        />
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default TalentPoolManage;